import { WIDGET_KIND_META } from "../widgetKind";
import type {
  GroupColorSlot,
  GroupId,
  SymbolGroup,
  WorkspaceWidget,
} from "../workspace/workspace.types";
import { GROUP_IDS, getGroupLetter } from "../workspace/workspace.utils";
import { GROUP_COLOR_SLOTS } from "./groupSelect.constants";

export interface GroupSelectMembership {
  memberCounts: Partial<Record<GroupId, number>>;
  linkedWidgetsByGroup: Partial<Record<GroupId, string>>;
}

export interface GroupSelectDeriveInput {
  groups: SymbolGroup[];
  activeGroupId: GroupId | null;
  memberCounts: Partial<Record<GroupId, number>>;
  linkedWidgetsByGroup: Partial<Record<GroupId, string>>;
  selectNone: () => void;
  selectGroup: (groupId: GroupId) => void;
  createGroup: () => void;
  deleteGroup: (groupId: GroupId) => void;
  setGroupName: (groupId: GroupId, name: string) => void;
  setGroupColor: (groupId: GroupId, colorSlot: GroupColorSlot) => void;
}

export interface GroupSelectDerived {
  activeGroup: SymbolGroup | null;
  groups: SymbolGroup[];
  canCreate: boolean;
  canDelete: boolean;
  colorOptions: readonly GroupColorSlot[];
  memberCounts: Partial<Record<GroupId, number>>;
  linkedWidgetsByGroup: Partial<Record<GroupId, string>>;
  onSelectNone: () => void;
  onSelectGroup: (groupId: GroupId) => void;
  onCreateGroup: () => void;
  onDeleteGroup: (groupId: GroupId) => void;
  onRenameGroup: (groupId: GroupId, name: string) => void;
  onChangeGroupColor: (groupId: GroupId, colorSlot: GroupColorSlot) => void;
}

const MEMBER_LABEL_MAX = 13;

/** Duplicate-name compare; nameless groups are "" so they do not collide with "Group A". */
const duplicateNameSource = (group: SymbolGroup): string =>
  group.name?.trim() ?? "";

export const getGroupDisplayName = (group: SymbolGroup): string =>
  group.name?.trim() || `Group ${getGroupLetter(group.id)}`;

export const truncateGroupMemberLabel = (label: string): string =>
  label.length <= MEMBER_LABEL_MAX
    ? label
    : `${label.slice(0, 8).trimEnd()}...`;

export const formatLinkedWidgetsLabel = (kindLabels: string[]): string =>
  [...kindLabels]
    .map(truncateGroupMemberLabel)
    .sort((a, b) => a.localeCompare(b))
    .join(", ");

export const deriveGroupSelectMembership = (
  widgets: Record<string, WorkspaceWidget>,
): GroupSelectMembership => {
  const memberCounts: Partial<Record<GroupId, number>> = {};
  const labels: Partial<Record<GroupId, string[]>> = {};
  for (const widget of Object.values(widgets)) {
    if (widget.groupId === null) {
      continue;
    }
    memberCounts[widget.groupId] = (memberCounts[widget.groupId] ?? 0) + 1;
    const label = WIDGET_KIND_META[widget.kind].label;
    labels[widget.groupId] = [...(labels[widget.groupId] ?? []), label];
  }
  const linkedWidgetsByGroup: Partial<Record<GroupId, string>> = {};
  for (const [rawId, kindLabels] of Object.entries(labels)) {
    linkedWidgetsByGroup[rawId as GroupId] = formatLinkedWidgetsLabel(
      kindLabels ?? [],
    );
  }
  return { memberCounts, linkedWidgetsByGroup };
};

export const deriveGroupSelect = ({
  groups,
  activeGroupId,
  memberCounts,
  linkedWidgetsByGroup,
  selectNone,
  selectGroup,
  createGroup,
  deleteGroup,
  setGroupName,
  setGroupColor,
}: GroupSelectDeriveInput): GroupSelectDerived => {
  const sortedGroups = [...groups].sort((a, b) => a.id.localeCompare(b.id));
  const activeGroup =
    activeGroupId !== null
      ? (groups.find((group) => group.id === activeGroupId) ?? null)
      : null;

  return {
    activeGroup,
    groups: sortedGroups,
    canCreate: sortedGroups.length < GROUP_IDS.length,
    canDelete: sortedGroups.length > 0,
    colorOptions: GROUP_COLOR_SLOTS,
    memberCounts,
    linkedWidgetsByGroup,
    onSelectNone: selectNone,
    onSelectGroup: selectGroup,
    onCreateGroup: createGroup,
    onDeleteGroup: deleteGroup,
    onRenameGroup: (groupId, name) => {
      const trimmed = name.trim();
      if (
        trimmed &&
        sortedGroups.some(
          (group) =>
            group.id !== groupId &&
            duplicateNameSource(group).toLowerCase() === trimmed.toLowerCase(),
        )
      ) {
        window.alert(
          `A group named "${trimmed}" already exists. Choose a different name.`,
        );
        return;
      }
      setGroupName(groupId, name);
    },
    onChangeGroupColor: setGroupColor,
  };
};
